import { TokenInfo, DecodedJWT } from './types';
import { jwtDecode } from 'jwt-decode';

/**
 * Check if user is a distributor based on token info
 * A user is a distributor if their operatorId equals their distributorId
 */
export function checkIsDistributor(tokenInfo: TokenInfo): boolean {
  try {
    const decoded = jwtDecode<DecodedJWT>(tokenInfo.appToken);
    const operatorId = (decoded.OperatorId || decoded.operatorId || decoded.actor)?.toString();
    const distributorId = (decoded.DistributorId || decoded.distributorId)?.toString();

    return operatorId && distributorId && operatorId === distributorId;
  } catch (error) {
    return false;
  }
}

/**
 * Extract token expiry date from JWT
 */
export function getTokenExpiry(token: string): Date | null {
  try {
    const decoded = jwtDecode<DecodedJWT>(token);
    return new Date(decoded.exp * 1000);
  } catch (error) {
    return null;
  }
}

/**
 * Check if JWT token is expired
 */
export function isTokenExpired(decodedToken: DecodedJWT, bufferSeconds: number = 60): boolean {
  const now = Date.now() / 1000;
  return (now + bufferSeconds) >= decodedToken.exp;
}

/**
 * Validate JWT token string
 */
export function validateJwtToken(
  token: string, 
  bufferSeconds: number = 60
): { valid: boolean; decoded?: DecodedJWT; reason?: string } {
  try {
    const decoded = jwtDecode<DecodedJWT>(token);
    
    if (!decoded.exp) {
      return { valid: false, reason: 'Token missing expiration' };
    }
    
    if (isTokenExpired(decoded, bufferSeconds)) {
      return { 
        valid: false, 
        decoded,
        reason: `Token expired at ${new Date(decoded.exp * 1000).toISOString()}` 
      };
    }
    
    return { valid: true, decoded };
  } catch (error) {
    return { 
      valid: false, 
      reason: error instanceof Error ? error.message : 'Failed to decode token' 
    };
  }
}
